import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState
} from "react";
import {
  Alert,
  BigInfoIcon,
  Button,
  Flex,
  Icon,
  isNullish,
  Menu,
  OverflowText,
  Pill,
  Table,
  Text,
  TooltipWrapper
} from "@theorgchartdev/storybook";
import { BuildAppendTableRFC, DownloadTableRFC, LookupRecord } from "@/Utils/Promises";
import { kebabCase } from "@/Utils/formatters";
import {
  enumObjectTypes,
  enumOverrideType,
  kNameID,
  kPersonID,
  kPositionID,
  kPositionTitleID,
  kTitleID,
  objTypeClassMap,
  SYSTEM_GENERATED_AUTHOR
} from "@coreapp/constants";
import { appstr } from "@coreapp/utils/appstr";
import * as StateLib from "@/Utils/StateLib";
import { GetWSMgr } from "@/Utils/StateLib";
import { DATA_SOURCES_TEST_PREFIX } from "@/appComponents/Blocks/Modals/SettingsModal/ChartSettings/DataSourceConfiguration/Utils";
import { QObjDBObject } from "@coreapp/CoreApp/QObjDBObject";
import { useTableManagerContext } from "@/providers/AppendTableProvider";
import cx from "classnames";
import { appstrp } from "@coreapp/utils/appstrp";
import { SelectableSearchMenu } from "@/appComponents/Blocks/Menus/BasicChartSearchMenu/SelectableSearchMenu";
import { useToast } from "@/providers/ToastProvider";
import { ChangePopover } from "@/appComponents/Navigation/RightSidebar/Planning/Activity/ChangePopover";
import { useChartSettingsContext } from "@/providers/ChartSettingsProvider";
import { IsPositionChart } from "@coreapp/utils/IsPositionChart";
import FieldTypePill from "@/appComponents/Blocks/Modals/SettingsModal/ChartSettings/FieldsFormulasConfiguration/FieldTypePill";
import SystemGeneratedFieldPill from "@/appComponents/Blocks/Cards/ProfileBaseballCard/SystemGeneratedFieldPill";
import { QUpdateTableRFC } from "@coreapp/CoreApp/QUpdateTableRFC";
import { Globals } from "@coreapp/CoreApp/Globals";

const iconMap = {
  [enumOverrideType.add]: "plus",
  [enumOverrideType.delete]: "xmark",
  [enumOverrideType.change]: "pen",
  [enumOverrideType.move]: "arrow-right-solid"
};

const pillColorMap = {
  [enumOverrideType.add]: "success",
  [enumOverrideType.delete]: "error",
  [enumOverrideType.change]: "warning",
  [enumOverrideType.move]: "info"
};

const dataTestIdPrefix = "append-table-editor-";
const newDataTestIdPrefix = `${DATA_SOURCES_TEST_PREFIX}-append-table-editor-`;

const GetObjClass = (tableMgr, objType = enumObjectTypes.holder) => {
  const objDB = tableMgr.fOverrideClass.GetObjDB();
  const className = objTypeClassMap[objType] || "Box";
  return objDB.GetClassByName(className);
};

export const TypeCell = ({ rowState }) => {
  const data = rowState?.data;
  const isPosition = data?.isPosition;
  const type = isPosition ? "position" : "employee";

  return (
    <div className="field-applies-to-pill">
      <FieldTypePill
        type={type}
        pillProps={{
          size: "regular",
          "data-testid": `${newDataTestIdPrefix}field-type-pill-${type}`
        }}
      />
    </div>
  );
};

const GetOverrideLabel = ({ tableMgr, override }) => {
  const objType = override.SmartGetFieldValue("objType") || enumObjectTypes.holder;
  const isPosition = objType === enumObjectTypes.position;
  const classDef = GetObjClass(tableMgr, objType);
  if (!classDef)
    return {
      displayLabel: appstr("Information unavailable") + ` (${override.SmartGetFieldValue("RefID")})`
    };

  const recordIDDef = classDef.GetRecordIDFieldDef();
  const recordKey = recordIDDef.GetFieldName();
  const nameKey = classDef.GetFieldDefByID("Name")?.GetFieldName() ?? null;
  const titleKey = classDef.GetTitleFieldDef()?.GetFieldName() ?? null;

  const refObj = override.SmartGetFieldValue("chartRefObj");
  const overrideRefID = override.SmartGetFieldValue("RefID");
  const overrideComment = override.SmartGetFieldValue("Comment") || "";
  const overrideType = override.SmartGetFieldValue("Type");

  let displayLabel = appstr("refnotfound") + " (" + overrideRefID + ")";

  if (displayLabel && refObj && refObj[nameKey] && (refObj[titleKey] || refObj[recordKey])) {
    displayLabel = `${refObj[nameKey]} (${refObj[titleKey] ?? refObj[recordKey]})`;
  } else if (overrideType === "Add") {
    const { fDelta } = override.fChanges[0];
    const name = fDelta[nameKey];
    const title = fDelta[titleKey];
    const recordID = fDelta[recordKey];

    if (isPosition) {
      displayLabel = title ?? appstr("refnotfound");
      if (recordID) {
        displayLabel += ` (${recordID})`;
      }
    } else {
      displayLabel = name ?? appstr("refnotfound");
      if (title || recordID) {
        displayLabel += ` (${title ?? recordID})`;
      }
    }
  } else {
    const targetObj = classDef.FindObject(recordIDDef, overrideRefID, false);
    if (targetObj) {
      displayLabel = targetObj.FormatEmployeeLabel();
    }
  }

  return { displayLabel, overrideComment };
};

export const ModificationsTab = forwardRef(({ tableMgr }, ref) => {
  const {
    setTableManager,
    tableEditorContextValue: { showTableEditor, onEditRecord }
  } = useTableManagerContext();

  const { addToast } = useToast();
  const { chartConfigRef } = useChartSettingsContext();
  const currentChartConfig = chartConfigRef.current;

  const [currentEntryTarget, setCurrentEntryTarget] = useState(null);
  const [isLoadingRecords, setLoadingRecords] = useState(false);
  const [recordsCached, setRecordsCached] = useState({
    [enumObjectTypes.holder]: [],
    [enumObjectTypes.position]: []
  });

  const isPositionChart = IsPositionChart(currentChartConfig);

  const hasPositionOverrides = useMemo(() => {
    return tableMgr?.fOverrides?.some(override => {
      const objType = override.SmartGetFieldValue("objType") || enumObjectTypes.holder;
      return objType === enumObjectTypes.position;
    });
  }, [tableMgr?.fOverrides]);

  const positionClassIsDefined = useMemo(() => {
    const positionClass = GetObjClass(tableMgr, enumObjectTypes.position);
    return !isNullish(positionClass);
  }, [tableMgr]);

  const effectiveIsPositionChart = isPositionChart && positionClassIsDefined;
  useImperativeHandle(ref, () => ({
    validate: async () => {
      return true;
    },
    save: async () => {
      try {
        return true;
      } catch (error) {
        return false;
      }
    },
    checkValidity: async () => {
      return true;
    },
    hasUnsavedChanges: async () => {
      return false;
    }
  }));

  useEffect(() => {
    LoadRecords();
  }, [showTableEditor, tableMgr?.fOverrides?.length]);

  const LoadRecords = async () => {
    if (isLoadingRecords) return;
    try {
      setLoadingRecords(true);

      const employeeIds = [];
      const positionIds = [];

      tableMgr.fOverrides.forEach(override => {
        if (override.SmartGetFieldValue("Type") === enumOverrideType.add) {
          return;
        }

        const objType = override.SmartGetFieldValue("objType") || enumObjectTypes.holder;
        const recordID = override.SmartGetFieldValue("RefID");
        const objClass = GetObjClass(tableMgr, objType);

        if (!objClass) return;

        const recordIDFieldDef = objClass.GetRecordIDFieldDef();
        const existingDBObj = objClass.FindObject(recordIDFieldDef, recordID, true);

        if (isNullish(existingDBObj)) {
          if (objType === enumObjectTypes.position) {
            positionIds.push(recordID);
          } else {
            employeeIds.push(recordID);
          }
        }
      });

      if (employeeIds.length > 0) {
        const records = await LookupRecord(employeeIds, currentChartConfig["ID"], true, false);
        const boxClass = tableMgr.GetBoxClass();
        for (const record of records) {
          const dbObj = boxClass.AddNewObject();
          boxClass.ProcessObject(dbObj, record);
        }
      }

      if (positionIds.length > 0) {
        const records = await LookupRecord(positionIds, currentChartConfig["ID"], true, true);
        const positionClass = GetObjClass(tableMgr, enumObjectTypes.position);
        if (!positionClass) return;

        for (const record of records) {
          const dbObj = positionClass.AddNewObject();
          positionClass.ProcessObject(dbObj, record);
        }
      }
    } finally {
      setLoadingRecords(false);
    }
  };

  const handleSearchSelect = option => {
    let objType = option.result?.objType || option.objType || enumObjectTypes.holder;
    if (objType === enumObjectTypes.person) objType = enumObjectTypes.holder;

    const objClass = GetObjClass(tableMgr, objType);
    if (!objClass) return;

    const recordIDDef = objClass.GetRecordIDFieldDef();
    const existingDbObj = objClass.FindObject(recordIDDef, option.value, true);
    if (!existingDbObj) {
      const dbObj = new QObjDBObject();
      dbObj.InitObject(objClass);
      objClass?.ProcessObject(dbObj, option.result);
      setRecordsCached(prev => ({
        ...prev,
        [objType]: [...prev[objType], dbObj]
      }));
    }

    if (!option.label && option.result) {
      const dbObj = existingDbObj || objClass.FindObject(recordIDDef, option.value, false);
      if (dbObj) {
        const labelObj = dbObj.FormatEmployeeLabel(null, true);
        option.label = labelObj?.primary || "";
      }
    }

    setCurrentEntryTarget(option);
  };

  const handleCreateOverride = type => {
    CreateExistingRecordEntry(currentEntryTarget, type);
  };

  const clearSearchTarget = () => {
    setCurrentEntryTarget(null);
  };

  const handleAddEmployee = () => {
    const { newDBObj, overrideObj, chartRefObj } = tableMgr.OpenOverrideEditor(
      null,
      null,
      null,
      enumOverrideType.add
    );
    onEditRecord(overrideObj, newDBObj, enumOverrideType.add, chartRefObj);
  };

  const handleAddPosition = () => {
    const { newDBObj, overrideObj, chartRefObj } = tableMgr.OpenOverrideEditor(
      null,
      null,
      null,
      enumOverrideType.add,
      enumObjectTypes.position // Pass position object type
    );
    overrideObj.SmartSetFieldValue("objType", enumObjectTypes.position);
    onEditRecord(overrideObj, newDBObj, enumOverrideType.add, chartRefObj);
  };

  const SanitizeLookupRecord = jsonRec => {
    let fieldMap = currentChartConfig["fields"];
    for (let field in jsonRec) {
      if (field in fieldMap) {
        jsonRec[fieldMap[field]] = jsonRec[field];
      }
    }
    return jsonRec;
  };

  const CreateExistingRecordEntry = (option, type) => {
    let dataObj = option.value instanceof QObjDBObject ? option.value : null;
    let recordData = {};
    let idKey = null;
    if (!dataObj) {
      let objType = option.result?.objType || option.objType || enumObjectTypes.holder;
      if (objType === enumObjectTypes.person) objType = enumObjectTypes.holder;
      if (recordsCached[objType]?.length > 0) {
        dataObj = recordsCached[objType].find(rec => rec.GetObjectRecordID() === option.value);
        recordData = dataObj?.JSON();
        idKey = dataObj?.GetParentClass().GetRecordIDFieldDef().GetFieldName();
      }
      if (dataObj == null) {
        const existingDBObjWrapper = tableMgr.LocateExistingDBObj(option.result, objType);
        dataObj = existingDBObjWrapper["dataObj"];
        recordData = existingDBObjWrapper["recordData"];
        idKey = existingDBObjWrapper["idKey"];
      }
    }

    if (!recordData?.[idKey])
      // may need to sanitize record here... backend search is trying to remove my projected keys in adv mode (searchPerson qmongo - queryperson is using fielddict for project array on backend) causing project keys to be overriten by fieldid's >:(
      recordData = SanitizeLookupRecord(recordData);
    const changeType = type;
    if (changeType) {
      const idForRecord = recordData?.[idKey];
      let correspondingOverride = null;
      if (idForRecord) {
        correspondingOverride = (tableMgr.fOverrides.filter(
          override => override.SmartGetFieldValue("RefID") == idForRecord
        ) || [null])[0];
        correspondingOverride &&
        correspondingOverride.SmartGetFieldValue("Type") != enumOverrideType.add
          ? correspondingOverride.SmartSetFieldValue("Type", changeType)
          : null;
      }
      const { newDBObj, overrideObj, chartRefObj } = tableMgr.OpenOverrideEditor(
        correspondingOverride,
        dataObj,
        recordData,
        type
      );
      onEditRecord(overrideObj, newDBObj, type, chartRefObj);
    }
    StateLib.ReRenderCanvas();
  };

  const EditEntry = async override => {
    if (!override) return;
    let type = override.SmartGetFieldValue("Type");
    let dataObj = null;
    let searchResult = null;
    let changeType = override.SmartGetFieldValue("Type") || enumOverrideType.add;
    let refObj = override.SmartGetFieldValue("chartRefObj");
    let objType = override.SmartGetFieldValue("objType") || enumObjectTypes.holder;
    var aClass = GetObjClass(tableMgr, objType);
    if (!aClass) return;

    if (changeType === enumOverrideType.add) {
      if (refObj) {
        searchResult = refObj;
      }
    } else {
      if (!refObj) {
        var objID = override.SmartGetFieldValue("RefID");
        var recordIDDef = aClass.GetRecordIDFieldDef();
        dataObj = aClass.FindObject(recordIDDef, objID, true);
      } else {
        searchResult = refObj;
      }
    }
    const { newDBObj, overrideObj, chartRefObj } = tableMgr.OpenOverrideEditor(
      override,
      dataObj,
      searchResult,
      type,
      objType
    );
    onEditRecord(overrideObj, newDBObj, type, chartRefObj);
  };

  const DeleteEntry = async override => {
    const override_id = override.SmartGetFieldValue("override_id");
    const appendTableID = tableMgr.fTableInfo["ID"];
    let data = { ID: appendTableID, override_id: override_id };
    const { overrides, code } = await BuildAppendTableRFC(data, "removeappendentry");
    addToast({ message: appstr("Changes have been saved"), status: "success" });
    tableMgr.LoadOverrides(overrides);
    setTableManager({ state: tableMgr });
  };

  const BuildTableData = useCallback(
    overrides => {
      if (!tableMgr || !overrides) return [];

      return overrides.map((override, index) => {
        const { displayLabel } = GetOverrideLabel({
          tableMgr,
          override
        });
        const type = override.SmartGetFieldValue("Type");
        const objType = override.SmartGetFieldValue("objType") || enumObjectTypes.holder;
        const isPosition = objType === enumObjectTypes.position;
        const recordTypeLabel = isPosition ? appstr("Position") : appstr("Employee");
        const override_id = override.SmartGetFieldValue("override_id");

        const lastChange = override.fChanges?.[override.fChanges.length - 1];
        const author = lastChange?.GetChangeAuthor() || "";
        const isSystemGenerated = author === SYSTEM_GENERATED_AUTHOR;

        return {
          override_id,
          recordType: recordTypeLabel,
          recordName: displayLabel,
          modificationType: type,
          override,
          objType,
          isPosition,
          isSystemGenerated
        };
      });
    },
    [tableMgr, isLoadingRecords]
  );

  const tableData = useMemo(
    () => BuildTableData(tableMgr?.fOverrides),
    [BuildTableData, tableMgr?.fOverrides, showTableEditor]
  );

  const RecordNameCell = ({ rowState }) => {
    const data = rowState?.data;
    const recordName = data?.recordName;
    const isSystemGenerated = data?.isSystemGenerated;

    return (
      <div
        style={{ display: "flex", gap: "8px", alignItems: "center", width: "100%", minWidth: 0 }}
      >
        <div style={{ flex: "1 1 auto", minWidth: 0, maxWidth: "fit-content" }}>
          <OverflowText size={"small"}>{recordName}</OverflowText>
        </div>
        {isSystemGenerated && (
          <div style={{ flexShrink: 0, minWidth: "200px" }}>
            <SystemGeneratedFieldPill forceRender={true} />
          </div>
        )}
      </div>
    );
  };

  const ModificationTypeCell = ({ rowState }) => {
    const data = rowState?.data;
    const type = data?.modificationType;
    const displayLabel = data?.recordName;
    const override = data?.override;

    return (
      <ChangePopover
        override={override}
        displayLabel={displayLabel}
        reference={
          <Pill
            data-testid={`${newDataTestIdPrefix}modification-pill-${kebabCase(type)}`}
            label={type}
            color={pillColorMap[type]}
            leftContent={<Icon name={iconMap[type]} size="xsmall" color="inherit" />}
            onClick={() => {}} // empty click for click styling
          />
        }
        isTableChange
      />
    );
  };

  const ActionsCell = ({ rowState }) => {
    const data = rowState?.data;
    const override = data?.override;
    const objType = override?.SmartGetFieldValue("objType") || enumObjectTypes.holder;
    const objClass = GetObjClass(tableMgr, objType);
    const isDisabled = !objClass;

    return (
      <div style={{ display: "flex", gap: "8px", justifyContent: "flex-end", flexShrink: 0 }}>
        <Button
          size={"small"}
          modifier={"tertiary"}
          iconProps={{ name: "pen-to-square" }}
          disabled={isDisabled}
          onClick={e => {
            e.stopPropagation();
            EditEntry(override);
          }}
          iconOnly
          data-testid={`${dataTestIdPrefix}edit-${data?.override_id}`}
        />
        <Button
          size={"small"}
          modifier={"tertiary"}
          iconProps={{ name: "trash-can" }}
          color="error"
          disabled={isDisabled}
          onClick={e => {
            e.stopPropagation();
            DeleteEntry(override);
          }}
          iconOnly
          data-testid={`${dataTestIdPrefix}delete-${data?.override_id}`}
        />
      </div>
    );
  };

  const columns = [
    {
      field: "recordType",
      headerName: appstr("Record type"),
      type: "cellRenderer",
      columnWidth: "twenty",
      CellRenderer: TypeCell
    },
    {
      field: "recordName",
      headerName: appstr("Record name"),
      type: "cellRenderer",
      columnWidth: "fifty",
      CellRenderer: RecordNameCell
    },
    {
      field: "modificationType",
      headerName: appstr("Modification type"),
      type: "cellRenderer",
      columnWidth: "twenty",
      CellRenderer: ModificationTypeCell
    },
    {
      field: "actions",
      headerName: "",
      type: "cellRenderer",
      columnWidth: "ten",
      CellRenderer: ActionsCell,
      filterDisabled: true,
      sortDisabled: true
    }
  ];

  const gridOptions = {};

  const handleModsUpload = (data = null) => {
    const numOverrides = tableMgr?.fOverrides?.length;
    if (numOverrides && !data) {
      const wsMgr = GetWSMgr();
      wsMgr.CreateReactAlert(
        appstr("Append table has existing records"),
        appstr("ReplaceOrAdd"),
        async () => {
          handleModsUpload({ replaceAll: true });
        },
        () => {
          handleModsUpload({ replaceAll: false });
        },
        appstr("Replace all"),
        appstr("Add to existing")
      );
      return;
    }
    doUpload(data);
  };

  const doUpload = data => {
    const wsMgr = GetWSMgr();

    let fileData = {};
    fileData["replaceAll"] = data != null ? data?.["replaceAll"] : false;
    fileData["directory"] = "Data";
    fileData["fileTypes"] = ["xls", "xlsx", "csv"];
    fileData["skipFM"] = true;

    fileData["dropTarget"] = {
      data: fileData,
      fTableMgr: tableMgr,
      fDropInfo: {
        dropEnabled: true,
        dropDirectory: "data",
        dropRestrictions: ["xls", "xlsx", "csv"],
        alertSuccess: true,
        locateHitElement: false,
        targetPanel: null
      },
      GetDropEnabled: function () {
        return this.fDropInfo["dropEnabled"];
      },
      GetDropInfo: function () {
        return this.fDropInfo;
      },
      ExecuteAlertCommand: function () {},
      HandlePostDropUpload: function (fileInfo) {
        const replaceAll = this.data != null ? this.data?.["replaceAll"] : false; // Upload params are null on drag-n-drop.
        const loadEntriesRFC = new QUpdateTableRFC();
        const tableID = tableMgr.fTableInfo["ID"];
        const recordID = isPositionChart
          ? currentChartConfig?.fields[kPositionID]
          : currentChartConfig?.fields[kPersonID];

        const recordName = isPositionChart ? appstr("Position Title") : appstr("Name");

        const jsonData = {
          file: fileInfo,
          ID: tableID,
          replaceAll: replaceAll,
          recordID: recordID,
          Name: recordName
        };

        loadEntriesRFC.InitUpdateTables(this);
        loadEntriesRFC.SetParams(
          Globals._wsMgr.GetAccountName(),
          Globals._wsMgr.GetUserName(),
          "uploadAppendTable",
          JSON.stringify(jsonData)
        );
        Globals._rfcMgr.QueueRFC(loadEntriesRFC);
      }
    };

    wsMgr.fReactUploadTarget = fileData["dropTarget"];
    wsMgr.UploadFile(fileData);
  };

  const handleModsDownload = () => {
    const tableID = tableMgr.fTableInfo["ID"];
    const recordIDVal = isPositionChart
      ? currentChartConfig?.fields[kPositionID]
      : currentChartConfig?.fields[kPersonID];
    var data = { tableID: tableID, recordID: recordIDVal };
    DownloadTableRFC(data, currentChartConfig["ID"]);
  };

  const customResultFormatter = (
    searchResult,
    doc,
    doDBResult,
    showHoldersAndPositionsSeparately
  ) => {
    if (!showHoldersAndPositionsSeparately) return null;

    const hasHolders = searchResult["__HOLDERS__"] && Array.isArray(searchResult["__HOLDERS__"]);
    const isPosition = hasHolders || searchResult["__IS_POSITION__"] === true;

    const mapping = currentChartConfig["fields"];

    const GetLabel = () => {
      if (doDBResult) {
        const iWizData = doc.GetIWizData();
        const objType = isPosition ? enumObjectTypes.position : enumObjectTypes.holder;
        const nameField = iWizData.GetNameField(objType);
        return searchResult[nameField] || "";
      }
      return "";
    };

    const GetSubLabel = () => {
      if (!doDBResult) return "";

      if (isPosition) {
        const positionIDField = mapping[kPositionID] || kPositionID;
        return searchResult[positionIDField] || "";
      } else {
        const titleField = mapping[kTitleID] || kTitleID;
        return searchResult[titleField] || "";
      }
    };

    return (
      <div className="oc-search-result">
        <div className="oc-search-result--content">
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              paddingLeft: isPosition ? "0" : "12px"
            }}
          >
            {!isPosition && <Icon name="arrow-turn-down-right" size="xsmall" />}
            <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
              <Icon name={isPosition ? "cubes" : "image-user"} size="xsmall" />
              <Flex gap={"4px"} alignItems={"center"}>
                <Text modifier={"medium"} size={"small"}>
                  {GetLabel()}
                </Text>
                {GetSubLabel() && (
                  <Text modifier={"lightgrey"} size={"xsmall"}>
                    {GetSubLabel()}
                  </Text>
                )}
              </Flex>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <Flex height={"100%"} direction={"column"} gap={"16px"}>
      {hasPositionOverrides && !positionClassIsDefined && (
        <Alert
          severity="warning"
          className={cx("oc-padding-small", "oc-text-small", "oc-data-source-details")}
        >
          {appstr("Position information unavailable")}
        </Alert>
      )}
      <Flex gap={"16px"} alignItems={"flex-start"}>
        <Text modifier={"default"} size={"small"}>
          {appstr("About append table (long)")}
        </Text>
        <Button
          data-testid={`${newDataTestIdPrefix}upload-button`}
          style={{ flexShrink: 0 }}
          modifier={"secondary"}
          iconProps={{ name: "cloud-arrow-up" }}
          onClick={() => handleModsUpload()}
        >
          {appstr("Upload")}
        </Button>
        <Button
          data-testid={`${newDataTestIdPrefix}download-button`}
          style={{ flexShrink: 0 }}
          modifier={"secondary"}
          iconProps={{ name: "arrow-down-to-line" }}
          disabled={!tableMgr?.fOverrides?.length}
          onClick={() => handleModsDownload()}
        >
          {appstr("Download")}
        </Button>
      </Flex>
      <Flex gap={"8px"} direction={"column"}>
        <Flex gap={"12px"} alignItems={"center"}>
          <div className={"oc-search-long"} style={{ flex: "1", minWidth: "0" }}>
            <SelectableSearchMenu
              dataTestId={`${dataTestIdPrefix}search-record`}
              inputSize="medium"
              listWidth={"fill"}
              displayRecents={false}
              selectedItem={currentEntryTarget}
              onRemoveItem={clearSearchTarget}
              searchMenuProps={{
                clearOnSelect: true,
                clearable: true,
                placeholder: currentEntryTarget ? "" : appstr("Search by name or title"),
                searchProps: {
                  onResetInput: clearSearchTarget
                },
                listWidth: "fill"
              }}
              projectFields={currentChartConfig?.headers}
              handleSearchOptionClick={handleSearchSelect}
              queryOptions={{ includeAux: false }}
              isPositionChart={effectiveIsPositionChart}
              handlePositions={effectiveIsPositionChart}
              showHoldersAndPositionsSeparately={effectiveIsPositionChart}
              customResultFormatter={customResultFormatter}
              hideFunctionalLabels={true}
              queryChartId={currentChartConfig["ID"]}
              mockDoc={tableMgr.GetDoc()}
            />
          </div>
          {effectiveIsPositionChart ? (
            <Menu
              trigger={
                <Button
                  style={{ flexShrink: "0" }}
                  className={"oc-append-table-add-type-button"}
                  color="success"
                  modifier={"quaternary"}
                  data-testid={`${dataTestIdPrefix}create-record`}
                  iconProps={{ name: "plus" }}
                >
                  {appstr("Add record")}
                  <Icon name="chevron-down" size="xsmall" style={{ width: 9, marginLeft: 8 }} />
                </Button>
              }
              dataTestId={`${dataTestIdPrefix}create-record`}
              listWidth={"fill"}
              value={""}
              options={[
                {
                  label: appstr("Employee"),
                  icon: "image-user",
                  value: enumObjectTypes.holder,
                  clickHandler: handleAddEmployee,
                  dataTestId: `${dataTestIdPrefix}add-employee`
                },
                {
                  label: appstr("Position"),
                  icon: "cubes",
                  clickHandler: handleAddPosition,
                  value: enumObjectTypes.position,
                  dataTestId: `${dataTestIdPrefix}add-position`
                }
              ]}
            />
          ) : (
            <Button
              style={{ flexShrink: "0" }}
              color="success"
              modifier={"quaternary"}
              onClick={handleAddEmployee}
              data-testid={`${dataTestIdPrefix}create-record`}
              iconProps={{ name: "plus" }}
            >
              {appstr("Add record")}
            </Button>
          )}
        </Flex>
        <Flex gap={"12px"} alignItems={"center"}>
          <Button
            color="warning"
            modifier={"quaternary"}
            disabled={isNullish(currentEntryTarget)}
            onClick={() => handleCreateOverride(enumOverrideType.change)}
            data-testid={`${dataTestIdPrefix}change-record`}
            iconProps={{ name: "pen" }}
          >
            {appstr("Change")}
          </Button>
          <Button
            color="info"
            modifier={"quaternary"}
            disabled={isNullish(currentEntryTarget)}
            onClick={() => handleCreateOverride(enumOverrideType.move)}
            data-testid={`${dataTestIdPrefix}move-record`}
            iconProps={{ name: "arrow-right-solid" }}
          >
            {appstr("Move")}
          </Button>
          <Button
            color="error"
            modifier={"quaternary"}
            disabled={isNullish(currentEntryTarget)}
            onClick={() => handleCreateOverride(enumOverrideType.delete)}
            data-testid={`${dataTestIdPrefix}delete-record`}
            iconProps={{ name: "xmark" }}
          >
            {appstr("Delete")}
          </Button>
        </Flex>
      </Flex>
      {tableData?.length > 0 ? (
        <Flex direction={"column"} flex={"1"} overflow={"hidden"} gap={"16px"}>
          <Flex gap={"8px"} alignItems={"center"}>
            <Text modifier={"bold"} size={"small"}>
              {appstrp("Modification history", ":")}
            </Text>
            <TooltipWrapper
              tooltipProps={{ message: appstr("Modification history tip") }}
              margin={0}
            >
              <Flex>
                <Icon name={"circle-info"} size={"small"} />
              </Flex>
            </TooltipWrapper>
          </Flex>
          <div style={{ flex: 1, overflow: "auto" }} className={"ocn-list-scrollbar"}>
            <Table
              tableLayout={"fixed"}
              gridOptions={gridOptions}
              columns={columns}
              data={tableData}
              showHeader
              sortEnabled
              filterEnabled
              containerPadding
              rowSize={"largeR"}
              textSize={"mediumT"}
            />
          </div>
        </Flex>
      ) : (
        <Flex alignItems={"center"} flex={"1"}>
          <BigInfoIcon
            message={appstr("Modifications shown here (long)")}
            name="table"
            color="brand"
          />
        </Flex>
      )}
    </Flex>
  );
});
